import React, { useEffect, useState } from "react";
import {
  Copy,
  HelpCircle,
  Plus,
  Trash2,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Unlink,
} from "lucide-react";
import "./MyShopsPage.css";
import { API_URL, apiClient } from "../../api/api";
import { useAuth } from "../../context/AuthContext";

const tutorialImages = [
  require("./tutorial_imgs/1.png"),
  require("./tutorial_imgs/2.png"),
  require("./tutorial_imgs/3.png"),
  require("./tutorial_imgs/4.png"),
  require("./tutorial_imgs/5.png"),
  require("./tutorial_imgs/6.png"),
  require("./tutorial_imgs/7.png"),
  require("./tutorial_imgs/8.png"),
  require("./tutorial_imgs/9.png"),
  require("./tutorial_imgs/10.png"),
];

const stepDescriptions = [
  "First click the '+ Add shop' button on this page and give your shop a title...",
  "Copy the generated affiliate manager email",
  "Navigate to Account Settings in the seller dashboard",
  "Navigate to User Management within Account Settings",
  "Click the 'Add User' button on the top right'",
  "Set role to 'Main Administrator'",
  'Select "All active countries"',
  "Paste the copied Manager Email in Email Address Input",
  "Set Default Language to UK English",
  "Click 'Activate' on the shop you just created...",
];

export default function MyShopsPage() {
  const { user, selectedShopId, setSelectedShopId, refreshShops, updateShopStatus } =
    useAuth();
  const canManageClientSlack = ["cs_internal", "super_admin"].includes(user?.role);
  const pollIntervalsRef = React.useRef({});
  const slackPollIntervalsRef = React.useRef({});
  const clientSlackPollIntervalsRef = React.useRef({});
  const pollCountsRef = React.useRef({});

  const [isSlackConnecting, setIsSlackConnecting] = useState({});
  const [isClientSlackConnecting, setIsClientSlackConnecting] = useState({});

  const handleConnectSlackShop = async (shopId) => {
    try {
      const response = await apiClient.get(
        `/slack/slack-oauth-url?shop_id=${shopId}`,
      );
      if (response.status === 200) {
        const result = response.data;
        if (result.oauth_url) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          const popup = window.open(
            result.oauth_url,
            "SlackConnection",
            `width=${width},height=${height},left=${left},top=${top},status=no,location=no,toolbar=no,menubar=no`,
          );

          if (popup) {
            setIsSlackConnecting((prev) => ({ ...prev, [shopId]: true }));

            // Start Polling for this specific shop
            if (slackPollIntervalsRef.current[shopId])
              clearInterval(slackPollIntervalsRef.current[shopId]);

            let attempts = 0;
            slackPollIntervalsRef.current[shopId] = setInterval(async () => {
              attempts++;
              if (attempts > 100) {
                // Timeout after ~5 mins
                clearInterval(slackPollIntervalsRef.current[shopId]);
                delete slackPollIntervalsRef.current[shopId];
                setIsSlackConnecting((prev) => ({ ...prev, [shopId]: false }));
                return;
              }

              // Fetch this specific shop to check Slack status
              try {
                const sResp = await apiClient.get(
                  `/api/shops/get-single?shopId=${shopId}`,
                );
                if (sResp.status === 200) {
                  const sData = sResp.data;
                  if (sData.shop?.slack_team_id) {
                    // Success!
                    clearInterval(slackPollIntervalsRef.current[shopId]);
                    delete slackPollIntervalsRef.current[shopId];
                    setIsSlackConnecting((prev) => ({
                      ...prev,
                      [shopId]: false,
                    }));
                    // Update shops state to show "Connected"
                    setShops((prev) =>
                      prev.map((s) =>
                        s._id === shopId ? { ...s, ...sData.shop } : s,
                      ),
                    );
                    // Also refresh global state if needed
                    refreshShops();
                  }
                }
              } catch (e) {
                console.error("Polling error", e);
              }
            }, 3000);
          }
        }
      }
    } catch (error) {
      console.error("Failed to initiate Slack connection", error);
    }
  };

  const handleDisconnectSlackShop = async (shopId) => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect Slack for this shop? This will stop all alerts and reports for this shop.",
      )
    ) {
      return;
    }

    try {
      const response = await apiClient.post(
        `/slack/disconnect-shop?shop_id=${shopId}`,
      );

      if (response.status === 200) {
        const result = response.data;
        if (result.status === "success") {
          // Update local state
          setShops((prev) =>
            prev.map((s) =>
              s._id === shopId
                ? {
                    ...s,
                    slack_team_id: null,
                    slack_channel_id: null,
                    slack_access_token: null,
                    slack_team_name: null,
                    slack_channel: null,
                  }
                : s,
            ),
          );
          toast({
            title: "Success",
            description: "Slack disconnected successfully for this shop.",
          });
        }
      }
    } catch (error) {
      console.error("Failed to disconnect Slack for shop", error);
      toast({
        title: "Error",
        description: "Failed to disconnect Slack. Please try again.",
      });
    }
  };

  const handleConnectClientSlackShop = async (shopId) => {
    try {
      const response = await apiClient.get(
        `/slack/slack-oauth-url?shop_id=${shopId}&purpose=client`,
      );
      if (response.status === 200 && response.data.oauth_url) {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          response.data.oauth_url,
          "ClientSlackConnection",
          `width=${width},height=${height},left=${left},top=${top},status=no,location=no,toolbar=no,menubar=no`,
        );

        if (popup) {
          setIsClientSlackConnecting((prev) => ({ ...prev, [shopId]: true }));

          if (clientSlackPollIntervalsRef.current[shopId])
            clearInterval(clientSlackPollIntervalsRef.current[shopId]);

          let attempts = 0;
          clientSlackPollIntervalsRef.current[shopId] = setInterval(async () => {
            attempts++;
            if (attempts > 100) {
              clearInterval(clientSlackPollIntervalsRef.current[shopId]);
              delete clientSlackPollIntervalsRef.current[shopId];
              setIsClientSlackConnecting((prev) => ({ ...prev, [shopId]: false }));
              return;
            }

            try {
              const sResp = await apiClient.get(
                `/api/shops/get-single?shopId=${shopId}`,
              );
              if (sResp.status === 200 && sResp.data.shop?.client_slack_team_id) {
                clearInterval(clientSlackPollIntervalsRef.current[shopId]);
                delete clientSlackPollIntervalsRef.current[shopId];
                setIsClientSlackConnecting((prev) => ({ ...prev, [shopId]: false }));
                setShops((prev) =>
                  prev.map((sh) =>
                    sh._id === shopId ? { ...sh, ...sResp.data.shop } : sh,
                  ),
                );
                refreshShops();
              }
            } catch (e) {
              console.error("Polling error", e);
            }
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Failed to initiate client Slack connection", error);
      toast({
        title: "Error",
        description: "Could not start the client Slack connection.",
      });
    }
  };

  const handleDisconnectClientSlackShop = async (shopId) => {
    if (
      !window.confirm(
        "Disconnect the client Slack channel for this shop? Client reports will stop going to it.",
      )
    ) {
      return;
    }

    try {
      const response = await apiClient.post(
        `/slack/disconnect-client-shop?shop_id=${shopId}`,
      );
      if (response.status === 200 && response.data.status === "success") {
        setShops((prev) =>
          prev.map((sh) =>
            sh._id === shopId
              ? {
                  ...sh,
                  client_slack_team_id: null,
                  client_slack_channel_id: null,
                  client_slack_access_token: null,
                  client_slack_team_name: null,
                  client_slack_channel: null,
                }
              : sh,
          ),
        );
        toast({
          title: "Success",
          description: "Client Slack channel disconnected for this shop.",
        });
      }
    } catch (error) {
      console.error("Failed to disconnect client Slack for shop", error);
      toast({
        title: "Error",
        description: "Failed to disconnect the client Slack channel. Please try again.",
      });
    }
  };

  const toast = ({ title, description }) => {
    window.alert(`${title}\n${description || ""}`);
  };

  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  const [userData, setUserData] = useState(null);
  const [shops, setShops] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [isFetchingShops, setIsFetchingShops] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [newShopName, setNewShopName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [customManager, setCustomManager] = useState("");
  const [customPass, setCustomPass] = useState("");
  const [customLinked, setCustomLinked] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [shopToDelete, setShopToDelete] = useState(null);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [createdShopEmail, setCreatedShopEmail] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loadingShops, setLoadingShops] = useState({});

  // Resume polling on load
  useEffect(() => {
    shops.forEach((shop) => {
      const status = shop.activationStatus?.toLowerCase();
      if (
        (status === "activating" || status === "syncing") &&
        !pollIntervalsRef.current[shop._id]
      ) {
        console.log(`Resuming poll for shop ${shop._id} with status ${status}`);
        pollActivationStatus(shop._id);
      }
    });
  }, [shops]);
  const [showLinkAlert, setShowLinkAlert] = useState(false);
  const [addShopType, setAddShopType] = useState(null); // 'new' or 'existing'
  const [createStep, setCreateStep] = useState("selection"); // 'selection' | 'form'

  const fetchUserAndShops = async () => {
    try {
      // 1. Fetch User Profile (uses Cookie + Authorization header)
      const response = await apiClient.get("/user/profile");
      if (response.status === 200) {
        const result = response.data;
        if (result.status === "success" && result.user) {
          setUserData(result.user);
          const userId = result.user.id;

          // Ensure localStorage is synced
          localStorage.setItem("userId", userId);

          // 2. Fetch Shops using the ID we just got
          fetchShops(userId);
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error.message);
    }
  };

  const fetchShops = async (userId) => {
    if (!userId) {
      userId = localStorage.getItem("userId");
    }
    if (!userId) return;

    setIsFetchingShops(true);
    try {
      const url = `/api/shops/get?userId=${userId}&search=${encodeURIComponent(
        debouncedSearchTerm,
      )}&page=${page}&pageSize=${pageSize}`;
      const response = await apiClient.get(url);
      if (response.status === 200) {
        const result = response.data;
        setShops(result.shops || []);
        // API should ideally return total_pages or count. Fallback to 1 if not.
        setTotalPages(result.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching shops:", error.message);
    } finally {
      setIsFetchingShops(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    // If page/search changes, we need userId to refetch
    fetchShops();
  }, [page, debouncedSearchTerm]);

  useEffect(() => {
    fetchUserAndShops();
    const alertDismissed =
      localStorage.getItem("linkAlertDismissed") === "true";
    setShowLinkAlert(!alertDismissed);
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAuthorize = async (shopId) => {
    try {
      const response = await apiClient.get(
        `/tiktok/auth_url?shop_id=${shopId}`,
      );
      if (response.status === 200) {
        const { authorization_url } = response.data;
        window.location.href = authorization_url;
      }
    } catch (error) {
      console.error("Failed to initiate authorization", error);
      toast({
        title: "Authorization Failed",
        description: "Could not start authorization flow.",
      });
    }
  };

  const checkLink = async (shop) => {
    // Show info popup
    toast({
      title: "Activation Started",
      description:
        "It will take 15-20 minutes to activate the account and sync the data.",
      duration: 10000, // Disappear after 10 seconds
    });

    await apiClient.post("/api/shops/link", { shop_id: shop["_id"] });
    await fetchShops();
    pollActivationStatus(shop._id);
  };

  const handleSync = async (shop) => {
    toast({
      title: "Syncing Started",
      description:
        "Data scraping is being Started. This may take a few minutes.",
    });

    try {
      const response = await apiClient.post("/api/shops/sync", {
        shop_id: shop._id,
      });

      if (response.status === 200) {
        await fetchShops();
        pollActivationStatus(shop._id);
      } else {
        const errorData = response.data;
        toast({
          title: "Sync Failed",
          description: errorData.detail || "Could not start sync task.",
        });
      }
    } catch (error) {
      console.error("Sync error:", error);
    }
  };

  const pollActivationStatus = (shopId) => {
    // Clear existing for this shop if any
    if (pollIntervalsRef.current[shopId]) {
      clearInterval(pollIntervalsRef.current[shopId]);
    }
    pollCountsRef.current[shopId] = 0;

    const poll = async () => {
      try {
        const response = await apiClient.get(
          `/api/shops/poll-activation-status?shop_id=${shopId}`,
        );
        const data = response.data;

        // Dynamically update shop status in UI
        if (data.activationStatus) {
          updateShopStatus(shopId, data.activationStatus);
          setShops((prevShops) =>
            prevShops.map((s) =>
              s._id === shopId
                ? { ...s, activationStatus: data.activationStatus }
                : s,
            ),
          );
        }

        if (data.taskStatus === "STOPPED") {
          clearInterval(pollIntervalsRef.current[shopId]);
          delete pollIntervalsRef.current[shopId];
          await fetchShops(); // Final refresh
        }

        pollCountsRef.current[shopId] =
          (pollCountsRef.current[shopId] || 0) + 1;
        // Increase polling limit to ~30 mins (450 * 4s = 1800s)
        if (pollCountsRef.current[shopId] > 450) {
          clearInterval(pollIntervalsRef.current[shopId]);
          delete pollIntervalsRef.current[shopId];
          await fetchShops();
        }
      } catch (error) {
        // quiet fail
      }
    };

    poll(); // immediate run
    pollIntervalsRef.current[shopId] = setInterval(poll, 4000);
  };

  useEffect(() => {
    return () => {
      // Cleanup all intervals
      if (pollIntervalsRef.current) {
        Object.values(pollIntervalsRef.current).forEach(clearInterval);
      }
      if (slackPollIntervalsRef.current) {
        Object.values(slackPollIntervalsRef.current).forEach(clearInterval);
      }
      if (clientSlackPollIntervalsRef.current) {
        Object.values(clientSlackPollIntervalsRef.current).forEach(clearInterval);
      }
    };
  }, []);

  const createShop = async () => {
    setCreateLoading(true);
    if (!newShopName) {
      toast({
        title: "Missing Name",
        description: "Shop name cannot be blank",
      });
      setCreateLoading(false);
      return;
    }
    try {
      let userId = localStorage.getItem("userId");
      if (!userId) {
        // Fallback if somehow missing
        toast({
          title: "Error",
          description: "User ID missing. Re-login required.",
        });
        setCreateLoading(false);
        return;
      }
      const response = await apiClient.post("/api/shops/create", {
        userId,
        customManager: addShopType === "existing" ? customManager : null,
        customPass: addShopType === "existing" ? customPass : null,
        customLinked: addShopType === "existing" ? false : customLinked,
        shopName: newShopName,
        region: "US",
      });
      const data = response.data;
      if (response.status === 200 || response.status === 201) {
        setIsCreateDialogOpen(false);
        const successEmail =
          addShopType === "existing"
            ? customManager
            : `${data.Shop.managerID}@${data.Shop.emailDomain}`;
        setCreatedShopEmail(successEmail);
        setIsSuccessDialogOpen(true);
        // Refresh global state (Header/Sidebar) and local table
        await refreshShops(userId);
        fetchShops(userId);
      } else {
        // Handle backend errors (e.g. 409 duplicate email)
        if (data && data.message) {
          toast({
            title: "Error Creating Shop",
            description: data.message,
          });
        }
      }
    } catch (error) {
      console.error("Error creating shop:", error.message);
    }
    setCreateLoading(false);
  };

  const deleteShop = async (shopID) => {
    setDeleteLoading(true);
    try {
      const response = await apiClient.delete(
        `/api/shops/delete?shopId=${shopID}`,
      );
      if (response.status === 200) {
        toast({ title: "Success", description: "Shop deleted successfully." });
        const uid = localStorage.getItem("userId");
        await refreshShops(uid);
        fetchShops();
      }
    } catch (error) {
      console.error("Error deleting shop:", error.message);
    }
    setDeleteLoading(false);
    setIsDeleteDialogOpen(false);
  };

  const toggleShopStatus = async (shop) => {
    setLoadingShops((prev) => ({ ...prev, [shop._id]: true }));
    try {
      await apiClient.post("/api/shops/updateStatus", {
        shopID: shop._id,
        isPaid: !shop.isPaid,
      });
      fetchShops();
    } finally {
      setLoadingShops((prev) => {
        const updated = { ...prev };
        delete updated[shop._id];
        return updated;
      });
    }
  };

  const getAvailableShops = () => {
    return (userData?.shopCount || 0) - activeCount;
  };

  const copyToClipboard = (email) => {
    navigator.clipboard.writeText(email);
    toast({
      title: "✅ Email copied",
      description: "The email has been copied to your clipboard.",
    });
  };

  const getShopRegionEmoji = (region) => {
    return region && region.toLowerCase() === "us" ? "🇺🇸" : "🇫🇷";
  };

  const dismissAlert = () => {
    setShowLinkAlert(false);
    localStorage.setItem("linkAlertDismissed", "true");
  };

  return (
    <div className="page-relative-wrapper">
      <div className="myshops-container">
        <div className="myshops-header">
          <div className="myshops-title-section">
            <h1 className="myshops-title">My Shops</h1>
            <p className="myshops-subtitle">
              Manage and monitor your connected shops
            </p>
          </div>
          <div className="myshops-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowTutorialDialog(true)}
            >
              <HelpCircle size={18} /> How to link your shop
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (
                  userData?.company_type?.toLowerCase() === "brand" &&
                  shops.length >= 1
                ) {
                  toast({
                    title: "Action Restricted",
                    description:
                      "Brand accounts can only connect one shop at a time.",
                  });
                  return;
                }
                setNewShopName("");
                setCustomManager("");
                setCustomPass("");
                setAddShopType(null);
                setCreateStep("selection");
                setIsCreateDialogOpen(true);
              }}
            >
              <Plus size={18} /> Add Shop
            </button>
          </div>
        </div>

        {showLinkAlert && (
          <div className="custom-alert">
            <AlertCircle size={24} color="#f59e0b" />
            <div className="alert-message">
              To access all ShopOS services, please ensure your shop is properly
              linked with an affiliate manager. Shops will become available
              after successful linking. If you need assistance, click the "How
              to link your shop" button.
            </div>
            <button className="btn-dismiss" onClick={dismissAlert}>
              Dismiss
            </button>
          </div>
        )}

        <div className="myshops-controls">
          <div className="search-wrapper">
            <Search className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search shops by name..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="table-controls">
            <button className="btn btn-secondary btn-small">
              <Filter size={14} /> Filter
            </button>
          </div>
        </div>

        <div className="table-section">
          <table
            className={`myshops-table ${isFetchingShops ? "shimmer skeleton" : ""}`}
          >
            <thead>
              <tr>
                <th>Authorize</th>
                <th>Shop Name</th>
                <th>Manager Email</th>
                <th>Slack</th>
                {canManageClientSlack && <th>Client Slack</th>}
                {/* <th># Bots</th> */}
                <th>Activation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shops.map((shop) => (
                <tr key={shop._id}>
                  <td>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => handleAuthorize(shop._id)}
                    >
                      Authorize
                    </button>
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {/* <span style={{ fontSize: "1.2rem" }}>{getShopRegionEmoji(shop?.region)}</span> */}
                      <span style={{ fontWeight: 600 }}>{shop.shopName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="email-copy-wrapper">
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() =>
                          copyToClipboard(
                            shop?.customEmail ||
                              `${shop.managerID}@${shop.emailDomain}`,
                          )
                        }
                      >
                        <Copy size={14} />
                      </button>
                      <span className="copy-text">
                        {shop?.customEmail ||
                          `${shop.managerID}@${shop.emailDomain}`}
                      </span>
                    </div>
                  </td>
                  <td>
                    {shop.slack_team_id ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          className="status-badge status-active channel-badge-truncate"
                          title={
                            shop.slack_channel
                              ? `#${shop.slack_channel}`
                              : shop.slack_team_name || "Connected"
                          }
                        >
                          {shop.slack_channel
                            ? `#${shop.slack_channel}`
                            : shop.slack_team_name || "Connected"}
                        </span>
                        <button
                          className="btn btn-outline-danger btn-small"
                          onClick={() => handleDisconnectSlackShop(shop._id)}
                          style={{ padding: "6px" }}
                          title="Disconnect Slack"
                        >
                          <Unlink size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className={`btn btn-small ${isSlackConnecting[shop._id] ? "btn-secondary" : "btn-primary"}`}
                        onClick={() => handleConnectSlackShop(shop._id)}
                        disabled={isSlackConnecting[shop._id]}
                      >
                        {isSlackConnecting[shop._id]
                          ? "Connecting..."
                          : "Connect"}
                      </button>
                    )}
                  </td>
                  {canManageClientSlack && (
                    <td>
                      {shop.client_slack_team_id ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            className="status-badge status-active channel-badge-truncate"
                            title={
                              shop.client_slack_channel
                                ? `#${shop.client_slack_channel}`
                                : shop.client_slack_team_name || "Connected"
                            }
                          >
                            {shop.client_slack_channel
                              ? `#${shop.client_slack_channel}`
                              : shop.client_slack_team_name || "Connected"}
                          </span>
                          <button
                            className="btn btn-outline-danger btn-small"
                            onClick={() => handleDisconnectClientSlackShop(shop._id)}
                            style={{ padding: "6px" }}
                            title="Disconnect client Slack channel"
                          >
                            <Unlink size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          className={`btn btn-small ${isClientSlackConnecting[shop._id] ? "btn-secondary" : "btn-primary"}`}
                          onClick={() => handleConnectClientSlackShop(shop._id)}
                          disabled={isClientSlackConnecting[shop._id]}
                        >
                          {isClientSlackConnecting[shop._id]
                            ? "Connecting..."
                            : "Connect"}
                        </button>
                      )}
                    </td>
                  )}
                  {/* <td>{shop.numBots || 0}</td> */}
                  <td>
                    {shop.activationStatus?.toLowerCase() === "activating" ? (
                      <span className="status-badge status-pending">
                        Activating
                      </span>
                    ) : shop.activationStatus?.toLowerCase() === "syncing" ? (
                      <span className="status-badge status-syncing">
                        Syncing
                      </span>
                    ) : shop.activationStatus?.toLowerCase() === "activated" ? (
                      <span className="status-badge status-active">
                        Activated
                      </span>
                    ) : shop.activationStatus?.toLowerCase() ===
                      "awaiting sync" ? (
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => handleSync(shop)}
                      >
                        Sync
                      </button>
                    ) : shop.activationStatus?.toLowerCase() ===
                      "sync failed" ? (
                      <button
                        className="btn btn-small"
                        style={{
                          backgroundColor: "#ef4444",
                          color: "white",
                          border: "none",
                        }}
                        onClick={() => handleSync(shop)}
                      >
                        Retry Sync
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => checkLink(shop)}
                      >
                        Activate
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-outline-danger btn-small"
                      onClick={() => {
                        setShopToDelete(shop);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination-footer">
            <span>Showing {shops.length} shops</span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <button className="page-btn active">{page}</button>
              <button
                className="page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Tutorial Modal */}
        {showTutorialDialog && (
          <div className="myshops-modal-overlay">
            <div
              className="myshops-modal-content"
              style={{
                maxWidth: "900px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <button
                className="myshops-modal-close-btn"
                onClick={() => setShowTutorialDialog(false)}
              >
                <Plus size={24} style={{ transform: "rotate(45deg)" }} />
              </button>
              <div className="myshops-modal-header">
                <h3 className="myshops-modal-title">
                  How to link your shop
                </h3>
                <p className="myshops-modal-description">
                  Follow these steps carefully to connect your shop
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                }}
              >
                {stepDescriptions.map((desc, i) => (
                  <div
                    key={i}
                    style={{
                      background: "rgba(255, 255, 255, 0.03)",
                      backdropFilter: "blur(10px)",
                      padding: "20px",
                      borderRadius: "20px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      <span
                        style={{
                          background:
                            "linear-gradient(90deg, rgba(153, 64, 89, 0.4) 0%, rgba(153, 64, 89, 0.2) 100%)",
                          color: "#fff",
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: "700",
                          flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </span>
                      <p
                        style={{
                          fontSize: "0.9rem",
                          lineHeight: "1.5",
                          margin: 0,
                          color: "#d4d4d8",
                        }}
                      >
                        {desc}
                      </p>
                    </div>
                    <img
                      src={tutorialImages[i]}
                      alt={`Step ${i + 1}`}
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        border: "1px solid #1f1f1f",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="myshops-modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTutorialDialog(false)}
                >
                  Close Guide
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {isSuccessDialogOpen && (
          <div className="myshops-modal-overlay">
            <div className="myshops-modal-content">
              <button
                className="myshops-modal-close-btn"
                onClick={() => setIsSuccessDialogOpen(false)}
              >
                <Plus size={24} style={{ transform: "rotate(45deg)" }} />
              </button>
              <div className="myshops-modal-header">
                <h2 className="myshops-modal-title">
                  {addShopType === "existing"
                    ? "Shop Added Successfully!"
                    : "Shop Created!"}
                </h2>
                <p className="myshops-modal-description">
                  {addShopType === "existing"
                    ? "Your existing shop has been connected and is ready for use."
                    : "Your shop has been successfully added. Now link it in your seller dashboard."}
                </p>
              </div>
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.03)",
                  backdropFilter: "blur(10px)",
                  padding: "24px",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    color: "#fff",
                    fontSize: "1.1rem",
                    flex: 1,
                    wordBreak: "break-all",
                  }}
                >
                  {createdShopEmail}
                </span>
                <button
                  className="btn btn-primary btn-small"
                  style={{ padding: "10px" }}
                  onClick={() => copyToClipboard(createdShopEmail)}
                >
                  <Copy size={20} />
                </button>
              </div>
              <div className="myshops-modal-footer">
                <button
                  className="btn btn-primary"
                  style={{ width: "100%", padding: "14px" }}
                  onClick={() => setIsSuccessDialogOpen(false)}
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Shop Modal */}
        {isCreateDialogOpen && (
          <div className="myshops-modal-overlay">
            <div className="myshops-modal-content">
              <button
                className="myshops-modal-close-btn"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                <Plus size={24} style={{ transform: "rotate(45deg)" }} />
              </button>

              {createStep === "selection" ? (
                <>
                  <div className="myshops-modal-header">
                    <h3 className="myshops-modal-title">Add Shop</h3>
                    <p className="myshops-modal-description">
                      Choose how you would like to add a shop to your account.
                    </p>
                  </div>
                  <div
                    className="shop-selection-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginTop: "20px",
                    }}
                  >
                    <button
                      className="selection-card"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "12px",
                        padding: "24px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onClick={() => {
                        setAddShopType("new");
                        setCreateStep("form");
                      }}
                    >
                      <div
                        style={{
                          background: "rgba(59, 130, 246, 0.2)",
                          padding: "12px",
                          borderRadius: "50%",
                        }}
                      >
                        <Plus size={24} color="#60a5fa" />
                      </div>
                      <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                        Add New Shop
                      </span>
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: "#a1a1aa",
                          textAlign: "center",
                        }}
                      >
                        Create a brand new shop setup from scratch.
                      </span>
                    </button>

                    <button
                      className="selection-card"
                      style={{
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "12px",
                        padding: "24px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onClick={() => {
                        setAddShopType("existing");
                        setCreateStep("form");
                      }}
                    >
                      <div
                        style={{
                          background: "rgba(16, 185, 129, 0.2)",
                          padding: "12px",
                          borderRadius: "50%",
                        }}
                      >
                        <Search size={24} color="#34d399" />
                      </div>
                      <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                        Add Existing Shop
                      </span>
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: "#a1a1aa",
                          textAlign: "center",
                        }}
                      >
                        Connect a shop that is already activated.
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="myshops-modal-header">
                    <h3 className="myshops-modal-title">
                      {addShopType === "existing"
                        ? "Add Existing Shop"
                        : "Add New Shop"}
                    </h3>
                    <p className="myshops-modal-description">
                      {addShopType === "existing"
                        ? "Enter the details of your activated shop."
                        : "Enter a name for your new shop to generate a manager email."}
                    </p>
                  </div>

                  {addShopType === "existing" && (
                    <div
                      className="custom-alert"
                      style={{
                        marginBottom: "20px",
                        background: "rgba(245, 158, 11, 0.1)",
                        border: "1px solid rgba(245, 158, 11, 0.2)",
                      }}
                    >
                      <AlertCircle size={20} color="#f59e0b" />
                      <span style={{ color: "#f59e0b", fontSize: "0.9rem" }}>
                        Only add the activated sub-shops here
                      </span>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Shop Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. My Awesome Shop"
                      value={newShopName}
                      onChange={(e) => setNewShopName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  {addShopType === "existing" && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Shop Email</label>
                        <input
                          type="email"
                          className="form-input"
                          placeholder="shop@example.com"
                          value={customManager}
                          onChange={(e) => setCustomManager(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Shop Password</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Enter shop password"
                          value={customPass}
                          onChange={(e) => setCustomPass(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  <div className="myshops-modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCreateStep("selection")}
                    >
                      Back
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ minWidth: "140px" }}
                      onClick={createShop}
                      disabled={createLoading}
                    >
                      {createLoading
                        ? "Processing..."
                        : addShopType === "existing"
                          ? "Add Shop"
                          : "Create Shop"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {isDeleteDialogOpen && (
          <div className="myshops-modal-overlay">
            <div className="myshops-modal-content">
              <button
                className="myshops-modal-close-btn"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                <Plus size={24} style={{ transform: "rotate(45deg)" }} />
              </button>
              <div className="myshops-modal-header">
                <h3
                  className="myshops-modal-title"
                  style={{ color: "#ef4444" }}
                >
                  Delete Shop?
                </h3>
                <p className="myshops-modal-description">
                  Are you sure you want to delete{" "}
                  <strong style={{ color: "#fff" }}>
                    {shopToDelete?.shopName}
                  </strong>
                  ? This action will remove all associated bot data.
                </p>
              </div>
              <div className="myshops-modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-outline-danger"
                  style={{ minWidth: "160px" }}
                  onClick={() => deleteShop(shopToDelete?._id)}
                >
                  {deleteLoading ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
